// Everything a page needs, built once after sign-in and handed to every renderer.
import type { ArcGISIdentityManager } from "@esri/arcgis-rest-request";
import { AdminApi, normaliseServerUrl, type FederatedServer } from "./api/admin";
import { ConfigService, type AppConfig } from "./api/config";
import { PortalApi } from "./api/portal";
import type { Me } from "./auth";

export interface AppContext {
  cfg: AppConfig;
  session: ArcGISIdentityManager;
  me: Me;
  config: ConfigService;
  admin: AdminApi;
  portal: PortalApi;
  /** Federated servers, fetched once per session; pages ask for them by admin URL. */
  servers(): Promise<FederatedServer[]>;
  server(serverUrl: string): Promise<FederatedServer>;
  /** The bundled .soe, for registering the SOI on a server (SPEC 8.5). */
  soePackage(): Promise<Blob>;
}

export function createContext(cfg: AppConfig, session: ArcGISIdentityManager, me: Me): AppContext {
  const admin = new AdminApi(session, cfg.portalUrl);
  let serversPromise: Promise<FederatedServer[]> | null = null;

  const servers = () => (serversPromise ??= admin.federatedServers());

  return {
    cfg,
    session,
    me,
    admin,
    config: new ConfigService(session, cfg, me),
    portal: new PortalApi(session, cfg.portalUrl),
    servers,
    async server(serverUrl: string): Promise<FederatedServer> {
      const wanted = normaliseServerUrl(serverUrl);
      const found = (await servers()).find((s) => normaliseServerUrl(s.url) === wanted);
      if (!found) throw new Error(`${wanted} is not a federated server of this Portal any more`);
      return found;
    },
    async soePackage(): Promise<Blob> {
      const res = await fetch(cfg.soeAssetPath, { cache: "no-store" });
      if (!res.ok) throw new Error(`${cfg.soeAssetPath} is missing from this deployment; run "npm run copy-soe" before building the Manager.`);
      return res.blob();
    }
  };
}
